import React, { useState, useEffect } from 'react';
import { ALL_SCENARIOS, DEFAULT_SCENARIOS, formatScenarioLabel } from '../../utils/elasticityCalculations';

/**
 * Settings panel for the Elasticity Heatmap view.
 * - Days to Exit slider (adapted from SimulationSettingsPanel)
 * - Scenario column toggles (multi-select buttons replacing the underlying gain slider)
 */
const HeatmapSettingsPanel = ({
  maxDays = 365,
  maxExpirationDate = null,
  daysToExit,
  onDaysChange,
  selectedScenarios,
  onScenariosChange,
  contractCount = 0,
  defaultExpanded = false,
  defaultDays = null,
  onReset = null,
  isOverridden = false, // true when user has moved the slider (fixed days mode)
  daysToExitMode = 'per-contract', // 'per-contract' | 'fixed' | 'percent'
  onModeChange = null,
  daysToExitPercent = null, // 0-1 float
  onPercentChange = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Detect if settings have changed from defaults
  const hasChanges = (() => {
    if (isOverridden) return true;
    if (daysToExitMode === 'percent') return true;
    if (defaultDays !== null && daysToExit !== defaultDays) return true;
    if (selectedScenarios.size !== DEFAULT_SCENARIOS.size) return true;
    for (const s of DEFAULT_SCENARIOS) {
      if (!selectedScenarios.has(s)) return true;
    }
    return false;
  })();

  // Clamp days if maxDays shrinks
  useEffect(() => {
    if (daysToExit > maxDays) {
      onDaysChange(maxDays);
    }
  }, [maxDays, daysToExit, onDaysChange]);

  const toggleExpanded = () => setIsExpanded(prev => !prev);

  const handleScenarioToggle = (scenarioPct) => {
    const next = new Set(selectedScenarios);
    if (next.has(scenarioPct)) {
      next.delete(scenarioPct);
    } else {
      next.add(scenarioPct);
    }
    onScenariosChange(next);
  };

  // Calculate exit date
  const getTargetDate = (days) => {
    const today = new Date();
    return new Date(today.getTime() + (days * 24 * 60 * 60 * 1000));
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateWithDays = (date, days) => `${formatDate(date)} (${days} d.)`;

  const getFormattedTargetDate = () => {
    const targetDate = getTargetDate(daysToExit);
    return formatDateWithDays(targetDate, daysToExit);
  };

  const getFormattedMaxDate = () => {
    if (maxExpirationDate) {
      const date = new Date(maxExpirationDate);
      return formatDateWithDays(date, maxDays);
    }
    const maxDate = getTargetDate(maxDays);
    return formatDateWithDays(maxDate, maxDays);
  };

  const selectedCount = selectedScenarios.size;

  return (
    <div className="simulation-settings bg-gray-800 rounded-lg mb-4">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-750 transition-colors"
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-200">
            Heatmap Settings
          </h3>
          <span className="text-xs text-gray-400">
            <span className="text-blue-500">
              {daysToExitMode === 'percent'
                ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of exp`
                : isOverridden ? `${daysToExit} days` : 'Per-contract days'
              }, {selectedCount} scenarios
            </span>
            {' '}({contractCount} contracts)
          </span>
        </div>
        <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
          ›
        </span>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Days to Exit Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-300">
                  Days to Exit
                </label>
                {/* Mode Toggle */}
                {onPercentChange && onModeChange && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        onModeChange('fixed');
                        onDaysChange(daysToExit || defaultDays || 45);
                      }}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        daysToExitMode === 'fixed' || daysToExitMode === 'per-contract'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      Fixed Days
                    </button>
                    <button
                      onClick={() => onPercentChange(daysToExitPercent || 0.30)}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        daysToExitMode === 'percent'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      % of Exp
                    </button>
                  </div>
                )}
              </div>
              <span className="text-sm font-bold text-blue-500">
                {daysToExitMode === 'percent'
                  ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of expiration`
                  : getFormattedTargetDate()
                }
              </span>
            </div>

            {daysToExitMode === 'percent' ? (
              /* Percentage slider: 5% to 100% */
              <div className="relative">
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={Math.round((daysToExitPercent || 0.30) * 100)}
                  onChange={(e) => onPercentChange(Number(e.target.value) / 100)}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${
                      ((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100
                    }%, #374151 ${((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>5%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            ) : (
              /* Fixed days slider (existing) */
              <div className="relative">
                <input
                  type="range"
                  min="1"
                  max={maxDays}
                  value={daysToExit}
                  onChange={(e) => onDaysChange(Number(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${
                      (daysToExit / maxDays) * 100
                    }%, #374151 ${(daysToExit / maxDays) * 100}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 d.</span>
                  <span>{Math.floor(maxDays / 2)} d.</span>
                  <span>{getFormattedMaxDate()}</span>
                </div>
              </div>
            )}
          </div>

          {/* Scenario Column Toggles */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-300">
              Underlying % Change Scenarios
            </label>
            <div className="flex flex-wrap gap-1">
              {ALL_SCENARIOS.map((scenarioPct) => {
                const isSelected = selectedScenarios.has(scenarioPct);
                return (
                  <button
                    key={scenarioPct}
                    onClick={() => handleScenarioToggle(scenarioPct)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {formatScenarioLabel(scenarioPct)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reset Button */}
          {hasChanges && onReset && (
            <div className="pt-2">
              <button
                onClick={onReset}
                className="w-1/3 px-4 py-2 text-sm font-medium rounded transition-colors bg-gray-700 text-gray-300 hover:bg-gray-600"
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}

      {/* Custom Slider Styles */}
      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]:focus {
          outline: none;
        }

        input[type="range"]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(33, 138, 255, 0.2);
        }
      `}</style>
    </div>
  );
};

export default HeatmapSettingsPanel;
